import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { SingletonRequest } from 'shared/api/lib'
import { showToast } from 'shared/ui'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { Rcs } from '../model'
import { RcsApi } from '../api'

export class RcsStore {
  private _rcs: Rcs | null = null
  private _loading = true
  private _rcsOrganizationInbox: Inbox | null = null

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get loading() {
    return this._loading
  }

  get isEmpty() {
    return !this._rcs
  }

  get rcs() {
    return this._rcs
  }

  get hasFallbackInboxId() {
    return !!this._rcs?.fallbackInboxId
  }

  get rcsOrganizationInbox() {
    return this._rcsOrganizationInbox
  }

  get hasRcsOrganizationInbox() {
    return !!this._rcsOrganizationInbox
  }

  syncRcs(rcs: Rcs) {
    if (this._rcs) {
      this._rcs.syncOrigin(rcs.origin)
      return
    }

    this._rcs = rcs
  }

  private _initRcs = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      const { data } = await RcsApi.getRcs()

      if (data?.[0]) {
        const rcs = new Rcs(data[0])
        this.syncRcs(rcs)
      }
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  private _initRcsOrganizationInbox = async () => {
    try {
      const { data } = await RcsApi.getRcsOrganizationInbox()

      if (data?.[0]) {
        const inbox = new Inbox(data[0], this._entities)
        runInAction(() => {
          this._rcsOrganizationInbox = inbox
        })
      }
    } catch (error) {
      logger.error(error)
    }
  }

  initRcs = new SingletonRequest(this._initRcs).request
  initRcsOrganizationInbox = new SingletonRequest(this._initRcsOrganizationInbox).request

  init = async () => {
    await Promise.all([this.initRcs(), this.initRcsOrganizationInbox()])
  }

  onUpdate = async (fallbackInbox: { id: number }) => {
    if (!this._rcs || this._rcs.fallbackInboxId === fallbackInbox.id) return

    try {
      const {
        data: { fallback_inbox_id },
      } = await RcsApi.updateRcs({
        fallback_inbox_id: fallbackInbox.id,
        id: this._rcs.id,
      })

      if (fallback_inbox_id && this._rcs) {
        this._rcs.updateFallbackInboxId(fallback_inbox_id)
      }
      showToast({
        type: 'success',
        title: 'Fallback number selected',
      })
    } catch (error) {
      logger.error(error)
    }
  }

  reset = () => {
    this._rcs = null
    this._loading = false
    this._rcsOrganizationInbox = null
  }
}
